import 'react-loading-skeleton/dist/skeleton.css'
import { addYears, format, subYears } from 'date-fns'
import { ja } from 'date-fns/locale'
import React, { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import styled from 'styled-components'
import { mutate } from 'swr'
import useSelectedDates from '../../hooks/useSelectedDates'
import CalendarMonth from './CalendarMonth'
import { useAuth } from 'hooks/use-auth'

type YearCalendarState = {
  year: Date
}

const YearCalendarWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 16px;
`

const YearCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-bottom: 32px;
`

const YearCalendarTitle = styled.div`
  font-size: 48px;
  margin-right: 16px;
`

const YearCalendarArrow = styled.button`
  font-size: 24px;
  font-weight: bold;
  margin-right: 16px;
  cursor: pointer;
  background: none;
  border: none;
`

const CalendarMonthTitle = styled.div`
  font-size: 24px;
`

const HolidayCalendar: React.FC = () => {
  const auth = useAuth();
  const [state, setState] = useState<YearCalendarState>({ year: new Date() })
  const { selectedDates: defaultSelectedDates } = useSelectedDates(state.year, auth.factoryId, auth.authorizationHeader)

  const handlePrevYearClick = () => setState({ year: subYears(state.year, 1) })
  const handleNextYearClick = () => setState({ year: addYears(state.year, 1) })

  useEffect(() => {
    mutate(['/api/selectedDates', state.year])
  }, [state.year])

  const months = Array.from({ length: 12 }, (_, index) => {
    const month = index
    const year = state.year.getFullYear()
    return new Date(year, month, 1)
  })

  return (
    <div>
      <YearCalendarHeader>
        <YearCalendarArrow onClick={handlePrevYearClick}>
          <svg
            width="14"
            height="21"
            viewBox="0 0 14 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.1755 0.791743C12.9916 0.620013 12.7731 0.483768 12.5326 0.390809C12.2921 0.29785 12.0343 0.25 11.7739 0.25C11.5136 0.25 11.2557 0.29785 11.0153 0.390809C10.7748 0.483768 10.5563 0.620013 10.3724 0.791743L1.2474 9.29456C1.0631 9.46594 0.916889 9.66951 0.817128 9.8936C0.717366 10.1177 0.666016 10.3579 0.666016 10.6005C0.666016 10.8432 0.717366 11.0834 0.817128 11.3075C0.916889 11.5316 1.0631 11.7352 1.2474 11.9065L10.3724 20.4094C10.5564 20.5809 10.7749 20.7169 11.0154 20.8097C11.2559 20.9025 11.5136 20.9503 11.7739 20.9503C12.0342 20.9503 12.292 20.9025 12.5324 20.8097C12.7729 20.7169 12.9914 20.5809 13.1755 20.4094C13.3595 20.2379 13.5055 20.0342 13.6051 19.8102C13.7047 19.5861 13.756 19.3459 13.756 19.1034C13.756 18.8608 13.7047 18.6207 13.6051 18.3966C13.5055 18.1725 13.3595 17.9689 13.1755 17.7974L5.46199 10.5913L13.1755 3.40372C13.9508 2.68126 13.9309 1.49568 13.1755 0.791743Z"
              fill="#0094B5"
            />
          </svg>
        </YearCalendarArrow>
        <YearCalendarTitle>{format(state.year, 'yyyy年', { locale: ja })}</YearCalendarTitle>
        <YearCalendarArrow onClick={handleNextYearClick}>
          <svg
            width="14"
            height="21"
            viewBox="0 0 14 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.824526 20.2083C1.00844 20.38 1.2269 20.5162 1.4674 20.6092C1.7079 20.7022 1.96571 20.75 2.22607 20.75C2.48644 20.75 2.74425 20.7022 2.98475 20.6092C3.22524 20.5162 3.44371 20.38 3.62762 20.2083L12.7526 11.7054C12.9369 11.5341 13.0831 11.3305 13.1829 11.1064C13.2826 10.8823 13.334 10.6421 13.334 10.3995C13.334 10.1568 13.2826 9.9166 13.1829 9.6925C13.0831 9.46841 12.9369 9.26484 12.7526 9.09346L3.62762 0.590645C3.44357 0.419142 3.22507 0.283094 2.98459 0.190277C2.74411 0.0974579 2.48637 0.0496864 2.22607 0.0496864C1.96578 0.0496864 1.70804 0.0974579 1.46756 0.190277C1.22708 0.283094 1.00858 0.419142 0.824526 0.590645C0.640472 0.76215 0.494473 0.965754 0.394864 1.18983C0.295255 1.41392 0.243985 1.65409 0.243985 1.89663C0.243985 2.13918 0.295255 2.37934 0.394864 2.60343C0.494473 2.82751 0.640471 3.03111 0.824526 3.20262L8.53801 10.4087L0.824526 17.5963C0.049201 18.3187 0.0690813 19.5043 0.824526 20.2083Z"
              fill="#0094B5"
            />
          </svg>
        </YearCalendarArrow>
      </YearCalendarHeader>
      <YearCalendarWrapper>
        {months.map((month) => (
          <div key={month.getMonth()}>
            <CalendarMonthTitle>
              &nbsp;{month.getMonth() + 1}&nbsp;
              <span style={{ fontSize: 12 }}>月</span>
            </CalendarMonthTitle>
            {defaultSelectedDates ? (
              <CalendarMonth date={month} defaultSelectedDates={defaultSelectedDates} />
            ) : (
              <div>
                <Skeleton
                  baseColor="#D3D3D3"
                  width={280}
                  height={200}
                  style={{ display: 'block' }}
                />
              </div>
            )}
          </div>
        ))}
      </YearCalendarWrapper>
    </div>
  )
}

export default HolidayCalendar
